import StatusIndicator from "./StatusIndicator";
import { useState } from "react";
import clsx from "clsx";
import { IconReload } from "../icons/IconReload";
import AuthGuard from "./AuthGuard";
import { PipelineStageTiming } from "./PipelineStageTiming";
import { collapseFullSuccessOnStageError } from "./collapseFullSuccessOnStageError";

type StatusKind = "skip" | "error" | "done" | "pending";
const statusColors: Record<StatusKind, string> = {
  done: "bg-green-500 dark:bg-green-600",
  error: "bg-red-500 dark:bg-red-600",
  pending: "bg-yellow-500 dark:bg-yellow-600",
  skip: "bg-gray-400 dark:bg-zinc-600",
};

export function DocumentProcessingStatus({
  title,
  onRetry,
  details,
  totalPages = 0,
  progress,
  hideFullSuccessOnStageError = false,
}: {
  title: string;
  onRetry?: () => Promise<unknown>;
  details?: React.ReactNode;
  /** DR-4850: parser, metadata, embeddings, and classification only. */
  hideFullSuccessOnStageError?: boolean;
  progress:
    | {
        status?: "done" | "error" | "skip" | "processing" | "pages-done";
        // DR-2426: the persisted id arrays are projected to counts server-side before
        // they cross the wire (they ran to thousands of UUIDs per stage on huge docs).
        // completedCount mirrors `new Set(completed).size` (deduped); the others mirror
        // raw `.length`. Preferred order below: live `counts` → these projected counts.
        completedCount?: number;
        erroredCount?: number;
        skippedCount?: number;
        // DR-2395: live per-page counts derived on read from the trigger columns.
        // Preferred over the projected counts (present on the 4 derived stages) when set.
        counts?: {
          done: number;
          processing: number;
          error: number;
          skip: number;
          pending: number;
        };
        errorMessage?: string;
        time?: number;
        startedAt?: string;
        completedAt?: string;
      }
    | undefined;
  totalPages: number | undefined;
}) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry?.();
    } catch (err) {
      console.error("Failed to retry:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsRetrying(false);
    }
  };

  // DR-2395: prefer the live per-page counts (from the trigger columns). DR-2426: fall
  // back to the projected array counts (completedCount = deduped size, the others = raw
  // length) — server-side replacements for the id arrays that used to cross the wire, so
  // the semantics are identical. `completed` is the total terminal count (done + skip +
  // error); the green segment below stays `completed - skipped - errored` (= counts.done).
  const counts = progress?.counts;
  const completed = counts
    ? counts.done + counts.skip + counts.error
    : (progress?.completedCount ?? 0);
  const skipped = counts ? counts.skip : (progress?.skippedCount ?? 0);
  const errored = counts ? counts.error : (progress?.erroredCount ?? 0);
  // DR-2872: the headline badge/tooltip shows "settled" work (done + skip), matching
  // PageProcessingSummary's settled() rule — an unresolved error should not read as done.
  // DR-3149: both subtractions are exact for the contract above (errored/skipped are subsets
  // of completed), so the clamp is an identity on every server payload. It only stops a
  // caller that builds disjoint counts by hand from rendering a negative page count.
  const settledCount = Math.max(0, completed - errored);
  const doneCount = Math.max(0, completed - skipped - errored);
  // The page counts stay true. A failed stage must not look fully successful.
  const shown = collapseFullSuccessOnStageError({
    enabled: hideFullSuccessOnStageError,
    status: progress?.status,
    totalPages,
    settledCount,
    doneCount,
    errored,
  });

  const statusesEntries: [string, number][] = [
    ["error", errored],
    ["skip", skipped],
    ["done", shown.doneCount],
  ];

  return (
    <div className="">
      <div className="flex flex-col space-y-1">
        <div className="group relative flex items-center justify-between space-x-2">
          <div className="flex items-center space-x-2">
            <StatusIndicator title={title} status={progress?.status} />
            <span className="text-gray-900 dark:text-zinc-100 font-medium">
              {title}{" "}
              <span className="text-sm text-gray-400 dark:text-zinc-500">
                ({formatTime(progress?.time)})
              </span>
            </span>
            {/* Duration is already shown via formatTime above (accumulated across
                pages); pass only the timestamps so this renders just "completed X ago".
                Suppressed while the stage is in-flight ('processing'/'pages-done' —
                both render the yellow indicator): completedAt still holds the
                page-rollup moment until the doc-level handler re-stamps it, so a
                completion label mid-run would be wrong. */}
            <PipelineStageTiming
              stage={
                progress?.status === "processing" ||
                progress?.status === "pages-done"
                  ? undefined
                  : {
                      startedAt: progress?.startedAt,
                      completedAt: progress?.completedAt,
                    }
              }
            />
          </div>

          <div className="flex items-center space-x-2">
            <AuthGuard roles={["admin", "curator"]}>
              {onRetry && (
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className={clsx(
                    "flex items-center gap-2 cursor-pointer group-hover:visible ml-2 px-2 py-1 dark:text-blue-400 dark:hover:text-blue-500 text-xs text-blue-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed",
                    !isRetrying && "invisible"
                  )}
                >
                  <IconReload className={isRetrying ? "animate-spin" : ""} />
                  {isRetrying ? "Retrying..." : "Retry"}
                </button>
              )}
            </AuthGuard>
            {!!totalPages && (
              <span
                className="text-gray-500 dark:text-zinc-400 text-sm"
                title={`${shown.settledCount} of ${totalPages}`}
              >
                {shown.settledCount}
              </span>
            )}
          </div>
        </div>

        {statusesEntries && (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="flex-1 bg-gray-200 dark:bg-zinc-800 rounded-full h-1.5 relative overflow-hidden">
              {statusesEntries.map(([status, count], index) => {
                const percentage =
                  totalPages > 0 ? (count / totalPages) * 100 : 0;
                const statusColor =
                  statusColors[status as keyof typeof statusColors] ||
                  "bg-gray-500 dark:bg-zinc-700";

                // Calculate left position based on previous segments
                const leftPosition = statusesEntries
                  .slice(0, index)
                  .reduce((acc, [_, prevCount]) => {
                    return acc + (prevCount / totalPages) * 100;
                  }, 0);

                return (
                  <div
                    key={status}
                    className={`absolute h-1.5 transition-all duration-300 ${statusColor}`}
                    title={`${status} ${count} of ${totalPages}`}
                    style={{
                      left: `${leftPosition}%`,
                      width: `${percentage}%`,
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {progress?.errorMessage && (
          <div className="ml-6 text-red-500 dark:text-red-400 text-sm bg-red-50 px-2 py-1 rounded flex items-center justify-between dark:bg-zinc-700 dark:border dark:border-zinc-700">
            <span className=" max-h-[150px] overflow-y-auto">
              {progress.errorMessage}
            </span>
          </div>
        )}

        {error && (
          <div className="ml-6 text-red-500 dark:text-red-400 text-sm bg-red-50 px-2 py-1 rounded flex items-center justify-between dark:bg-zinc-700 dark:border dark:border-zinc-700">
            <span>{error}</span>
          </div>
        )}

        {details}
      </div>
    </div>
  );
}

function formatTime(time: number | undefined) {
  if (time !== undefined) {
    if (time >= 60_000) {
      return `${(time / 60_000).toFixed(1)}m`;
    }

    return `${(time / 1000).toFixed(1)}s`;
  }

  return "N/A";
}
